import { useEffect, useMemo, useState } from "react";
import { BriefcaseBusiness, FolderKanban, PencilLine, Plus, Trash2 } from "lucide-react";

import type { Client, Project, ProjectSoftwareRule } from "@zentrack/domain";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  EmptyState,
  PageHeader,
  cn,
  toast,
} from "@zentrack/ui";

import { useWebApp } from "../app/app-provider";
import { ClientEditorDialog } from "../components/client-editor-dialog";
import { ConfirmDialog } from "../components/confirm-dialog";
import { ProjectEditorDialog } from "../components/project-editor-dialog";
import { EMPTY_SOFTWARE_RULES } from "../components/shared";
import { formatDateTime } from "../lib/date-helpers";

export function ClientsRoute() {
  const { api, perform, setErrorMessage, snapshot } = useWebApp();
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientDialog, setClientDialog] = useState<{
    mode: "create" | "edit";
    client?: Client;
  } | null>(null);
  const [projectDialog, setProjectDialog] = useState<{
    mode: "create" | "edit";
    project?: Project;
  } | null>(null);
  const [clientPendingDeletion, setClientPendingDeletion] = useState<Client | null>(null);
  const [projectPendingDeletion, setProjectPendingDeletion] = useState<Project | null>(null);

  useEffect(() => {
    if (!snapshot) {
      return;
    }

    if (selectedClientId && snapshot.clients.some((client) => client.id === selectedClientId)) {
      return;
    }

    setSelectedClientId(snapshot.clients[0]?.id ?? null);
  }, [selectedClientId, snapshot]);

  const selectedClient = snapshot?.clients.find((client) => client.id === selectedClientId) ?? null;
  const selectedProjects = useMemo(
    () =>
      snapshot ? snapshot.projects.filter((project) => project.clientId === selectedClient?.id) : [],
    [snapshot, selectedClient?.id],
  );
  const softwareRulesByProject = useMemo(() => {
    const grouped = new Map<string, ProjectSoftwareRule[]>();
    if (!snapshot) {
      return grouped;
    }

    for (const rule of snapshot.projectSoftwareRules) {
      grouped.set(rule.projectId, [...(grouped.get(rule.projectId) ?? []), rule]);
    }

    return grouped;
  }, [snapshot?.projectSoftwareRules]);

  if (!snapshot) {
    return null;
  }

  const handleClientSave = async (name: string) => {
    setErrorMessage(null);
    if (clientDialog?.mode === "edit") {
      const client = clientDialog.client;

      if (!client) {
        return;
      }

      await perform(() =>
        api.clients.update({
          id: client.id,
          name,
        }),
      );
      toast("Client updated.");
    } else {
      const created = await perform(() => api.clients.create({ name }));
      setSelectedClientId(created.id);
      toast("Client created.");
    }

    setClientDialog(null);
  };

  const handleProjectSave = async (name: string, softwareRules: ProjectSoftwareRule[]) => {
    if (!selectedClient) {
      return;
    }

    setErrorMessage(null);
    if (projectDialog?.mode === "edit") {
      const project = projectDialog.project;

      if (!project) {
        return;
      }

      await perform(() =>
        api.projects.update({
          id: project.id,
          clientId: selectedClient.id,
          name,
          softwareRules,
        }),
      );
      toast("Project updated.");
    } else {
      await perform(() =>
        api.projects.create({
          clientId: selectedClient.id,
          name,
          softwareRules,
        }),
      );
      toast("Project created.");
    }

    setProjectDialog(null);
  };

  return (
    <div className="flex flex-col gap-5 pb-10">
      <PageHeader
        actions={
          <Button onClick={() => setClientDialog({ mode: "create" })} type="button" variant="ghost">
            <Plus className="size-4" />
            New client
          </Button>
        }
        description="Manage clients and projects."
        title="Clients"
      />

      <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
        <Card>
          <CardContent className="px-3 pb-4 pt-4">
            {snapshot.clients.length === 0 ? (
              <EmptyState
                description="Create the first client to unlock projects and the timer."
                icon={BriefcaseBusiness}
                title="No clients yet"
              />
            ) : (
              <div className="flex flex-col gap-0.5">
                {snapshot.clients.map((client) => {
                  const projectCount = snapshot.projects.filter(
                    (project) => project.clientId === client.id,
                  ).length;
                  const isSelected = client.id === selectedClient?.id;

                  return (
                    <button
                      className={cn(
                        "flex items-center justify-between gap-3 rounded-md px-3 py-2.5 text-left transition-colors",
                        isSelected
                          ? "bg-secondary text-foreground"
                          : "text-muted-foreground hover:bg-secondary/60 hover:text-foreground",
                      )}
                      key={client.id}
                      onClick={() => setSelectedClientId(client.id)}
                      type="button"
                    >
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <p className="truncate text-sm font-medium text-foreground">{client.name}</p>
                        <p className="text-xs text-muted-foreground">
                          {projectCount} {projectCount === 1 ? "project" : "projects"}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          {selectedClient ? (
            <>
              <CardHeader className="flex-row items-center justify-between border-b border-border/60 pb-3">
                <div className="flex flex-col gap-0.5">
                  <p className="text-sm font-medium text-foreground">{selectedClient.name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedProjects.length}{" "}
                    {selectedProjects.length === 1 ? "project" : "projects"}
                  </p>
                </div>
                <div className="flex gap-1.5">
                  <Button
                    onClick={() => setProjectDialog({ mode: "create" })}
                    size="sm"
                    type="button"
                    variant="default"
                  >
                    <Plus className="size-3.5" />
                    New project
                  </Button>
                  <Button
                    onClick={() => setClientDialog({ mode: "edit", client: selectedClient })}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    <PencilLine className="size-3.5" />
                    Edit
                  </Button>
                  <Button
                    onClick={() => setClientPendingDeletion(selectedClient)}
                    size="sm"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 className="size-3.5" />
                    Delete
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {selectedProjects.length === 0 ? (
                  <div className="py-4">
                    <EmptyState
                      description="Create the first project under this client to start tracking time."
                      icon={FolderKanban}
                      title="No projects yet"
                    />
                  </div>
                ) : (
                  <div className="divide-y divide-border/50">
                    {selectedProjects.map((project) => {
                      const projectRules = softwareRulesByProject.get(project.id) ?? [];

                      return (
                        <div className="flex items-start justify-between gap-4 py-3" key={project.id}>
                          <div className="flex min-w-0 flex-col gap-1">
                            <div className="flex flex-wrap items-center gap-1.5">
                              <p className="text-sm font-medium text-foreground">{project.name}</p>
                              <Badge tone={projectRules.length > 0 ? "warning" : "accent"}>
                                {projectRules.length > 0
                                  ? `${projectRules.length} ${
                                      projectRules.length === 1 ? "app" : "apps"
                                    }`
                                  : "All apps"}
                              </Badge>
                            </div>
                            {projectRules.length > 0 ? (
                              <p className="text-xs text-muted-foreground">
                                {projectRules
                                  .map((rule) => rule.displayName)
                                  .slice(0, 3)
                                  .join(", ")}
                                {projectRules.length > 3 ? ` +${projectRules.length - 3} more` : ""}
                              </p>
                            ) : null}
                            <p className="text-xs text-muted-foreground">
                              Created{" "}
                              {formatDateTime(project.createdAt, {
                                dateStyle: "medium",
                                timeStyle: undefined,
                              })}
                            </p>
                          </div>
                          <div className="flex shrink-0 gap-1.5">
                            <Button
                              onClick={() => setProjectDialog({ mode: "edit", project })}
                              size="sm"
                              type="button"
                              variant="ghost"
                            >
                              <PencilLine className="size-3.5" />
                            </Button>
                            <Button
                              onClick={() => setProjectPendingDeletion(project)}
                              size="sm"
                              type="button"
                              variant="ghost"
                            >
                              <Trash2 className="size-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </>
          ) : (
            <CardContent className="pt-5">
              <EmptyState
                description="Create a client first, then add projects for it."
                icon={BriefcaseBusiness}
                title="No client selected"
              />
            </CardContent>
          )}
        </Card>
      </div>

      <ClientEditorDialog
        initialName={clientDialog?.client?.name}
        onOpenChange={(open) => {
          if (!open) {
            setClientDialog(null);
          }
        }}
        onSave={handleClientSave}
        open={Boolean(clientDialog)}
        title={clientDialog?.mode === "edit" ? "Edit client" : "New client"}
      />

      <ProjectEditorDialog
        clientName={selectedClient?.name ?? ""}
        initialName={projectDialog?.project?.name}
        initialSoftwareRules={
          projectDialog?.project
            ? softwareRulesByProject.get(projectDialog.project.id) ?? EMPTY_SOFTWARE_RULES
            : EMPTY_SOFTWARE_RULES
        }
        onOpenChange={(open) => {
          if (!open) {
            setProjectDialog(null);
          }
        }}
        onSave={handleProjectSave}
        open={Boolean(projectDialog)}
        trackingCapability={snapshot.trackingCapability}
        title={projectDialog?.mode === "edit" ? "Edit project" : "New project"}
      />

      <ConfirmDialog
        confirmLabel="Delete client"
        description={
          clientPendingDeletion
            ? `This will remove ${clientPendingDeletion.name} only if none of its projects have saved time entries or an active timer.`
            : ""
        }
        destructive
        onConfirm={async () => {
          if (!clientPendingDeletion) {
            return;
          }

          setErrorMessage(null);
          await perform(() =>
            api.clients.remove({
              id: clientPendingDeletion.id,
            }),
          );
          toast("Client deleted.");
          setClientPendingDeletion(null);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setClientPendingDeletion(null);
          }
        }}
        open={Boolean(clientPendingDeletion)}
        title="Delete client"
      />

      <ConfirmDialog
        confirmLabel="Delete project"
        description={
          projectPendingDeletion
            ? `This will remove ${projectPendingDeletion.name} only if it has no saved time entries or active timer.`
            : ""
        }
        destructive
        onConfirm={async () => {
          if (!projectPendingDeletion) {
            return;
          }

          setErrorMessage(null);
          await perform(() =>
            api.projects.remove({
              id: projectPendingDeletion.id,
            }),
          );
          toast("Project deleted.");
          setProjectPendingDeletion(null);
        }}
        onOpenChange={(open) => {
          if (!open) {
            setProjectPendingDeletion(null);
          }
        }}
        open={Boolean(projectPendingDeletion)}
        title="Delete project"
      />
    </div>
  );
}
